import {
  ActionIcon,
  Box,
  Collapse,
  Divider,
  Group,
  Loader,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { Suspense, useMemo } from "react";

import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { usePartComplexityCsvDownload } from "#modules/parts/view/usePartComplexityCsvDownload";
import { usePartJson } from "#modules/parts/view/usePartJson";
import { quoteRequestLineQuery } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { safeLazy } from "#shared/helpers/safeLazy";
import { Icon } from "#shared/icons/Icon";
import { AnalyticsEvent, useAnalytics } from "#shared/useAnalytics";
import { Stack } from "#shared/view/components/Stack";
import { AnalysisResultDimensionsSection } from "#shared/view/components/Thumbnails/AnalysisResult/AnalysisResultDimensionsSection";
import { AnalysisResultInformationSection } from "#shared/view/components/Thumbnails/AnalysisResult/AnalysisResultInformationSection";
import { ErrorThumbnail } from "#shared/view/components/Thumbnails/ErrorThumbnail";
import { isJobProcessing } from "shared";
import { extractInvalidFieldsFromMetadata } from "shared";

import { AnalysisResultComplexitySection } from "./AnalysisResultComplexitySection";

const PdfViewer = safeLazy(() =>
  import("../ThumbnailViewer/PdfViewer").then((m) => ({ default: m.PdfViewer })),
);

type Props = {
  part: PartDto;
  pdfUrl: string | null;
  quoteRequestLineId?: string;
};

export function AnalysisResult({ part, pdfUrl, quoteRequestLineId }: Props) {
  const { partJson, isLoading } = usePartJson(part.id);
  const { data: quoteRequestLine } = useQuery(
    quoteRequestLineQuery({ id: quoteRequestLineId ?? "" }),
  );
  const [opened, { toggle }] = useDisclosure(false);
  const analytics = useAnalytics();
  const { downloadCsv, canDownload: canDownloadComplexityCsv } = usePartComplexityCsvDownload(
    partJson?.complexity,
    part.name,
  );

  const isAnalyzing = isJobProcessing(part.analyze2DStatus) || isJobProcessing(part.analyzeStatus);

  const invalidFields = useMemo(
    () => extractInvalidFieldsFromMetadata(quoteRequestLine?.latestPredictionJob?.errorMetadata),
    [quoteRequestLine?.latestPredictionJob?.errorMetadata],
  );

  const handleToggle = () => {
    if (!opened) {
      analytics.capture(AnalyticsEvent.Part2DComplexityExpanded, { partId: part.id });
    }
    toggle();
  };

  const handleDownloadCsv = () => {
    analytics.capture(AnalyticsEvent.Part2DComplexityCsvExported, { partId: part.id });
    downloadCsv();
  };

  return (
    <Group h="100%" align="stretch" gap={0} wrap="nowrap">
      <Box w="30%" style={{ borderRight: "1px solid var(--mantine-color-gray-2)", flexShrink: 0 }}>
        <ScrollArea h="100%">
          {isLoading ? (
            <Group justify="center" p="xl">
              <Loader size="sm" />
            </Group>
          ) : partJson ? (
            <>
              <Stack p="md" gap={2}>
                <AnalysisResultInformationSection
                  part={part}
                  quoteRequestLine={quoteRequestLine ?? null}
                />
                <AnalysisResultDimensionsSection
                  part={part}
                  quoteRequestLine={quoteRequestLine ?? null}
                  invalidFields={invalidFields}
                />
              </Stack>

              <Divider />
              <Group justify="space-between" gap="xs" px="md" py="xs" wrap="nowrap">
                <UnstyledButton onClick={handleToggle} style={{ flex: 1, minWidth: 0 }}>
                  <Group justify="space-between" gap="xs" wrap="nowrap">
                    <Text size="xs" c="dimmed" fw={500}>
                      {opened ? "Cacher les données extraites" : "Voir les données extraites"}
                    </Text>
                    <Icon name={opened ? "chevronUp" : "chevronDown"} size={12} />
                  </Group>
                </UnstyledButton>
                {canDownloadComplexityCsv && (
                  <Tooltip label="Exporter les côtes en CSV">
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={handleDownloadCsv}
                      aria-label="Exporter les côtes en CSV"
                    >
                      <Icon name="download" size={14} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
              <Collapse expanded={opened}>
                <AnalysisResultComplexitySection complexity={partJson.complexity} />
              </Collapse>
            </>
          ) : isAnalyzing ? (
            <Stack align="center" justify="center" gap="xs" p="xl" h="100%">
              <Loader size="sm" />
              <Text size="sm" c="dimmed" ta="center">
                {t("part.analysis2D.analyzing")}
              </Text>
            </Stack>
          ) : null}
        </ScrollArea>
      </Box>
      <Box flex={1} miw={0}>
        {pdfUrl ? (
          <ErrorBoundary
            fallback={
              <Stack h="100%" align="center" justify="center" gap="xs">
                <Icon name="alertTriangle" size={24} color="red" />
                <Text size="sm" c="dimmed">
                  {t("qrl.preview.tooltip.png")}
                </Text>
              </Stack>
            }
          >
            <Suspense fallback={null}>
              <PdfViewer url={pdfUrl} />
            </Suspense>
          </ErrorBoundary>
        ) : (
          <ErrorThumbnail
            icon={"alertTriangle"}
            errorMessage={t("qrl.preview.tooltip.png")}
            part={part}
          />
        )}
      </Box>
    </Group>
  );
}
